import { Box, Button, ListItem, Text, UnorderedList, useBreakpointValue } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAllCourses, usePublishedItems } from '../../hooks/V2/useCourse';
import { useMutation } from 'react-query';
import { startCourse } from '../../apis/course';
import { useUserProfile } from '../../hooks/useUser';
import { axiosErrorHandler } from '../../apis/axios';
import useSnackBar from '../common/V2/Snackbar';
import { useDomainConfig } from '../../hooks/useDomainConfig';
import { SAGEX_ORG_KEY } from '../../utils/segexOrgName';

const fsdModules = [
	{
		themeColor: '#FEDFE5',
		name: 'HTML & CSS',
		description:
			"This foundational module introduces the building blocks of web pages. You'll learn how to structure content with HTML and style it with CSS, covering topics like the box model, positioning, and responsive design with media queries. A quick look into the topics we will be covering: ",
		duration: '12 hours',
		topics: [
			'HTML Introduction',
			'HTML & Styles',
			'HTML Box Model',
			'CSS Selectors',
			'Properties',
			'CSS Combinators',
			'CSS Flex & Grid',
			'CSS Media Queries',
		],
	},
	{
		themeColor: '#CFE9EE',
		name: 'Javascript',
		description:
			"Dive into the programming language of the web. This section covers everything from basic JavaScript syntax to complex features such as loops, functions, and event handling. You'll learn to manipulate the Document Object Model (DOM) and perform asynchronous operations. A quick look into the topics we will be covering: ",
		duration: '18 hours',
		topics: [
			'Javascript Introduction',
			'JS Operators, Taking Inputs & Markdown',
			'JS Operators & Conditional Statements',
			'JS Loops, Arrays & Strings',
			'Javascript Functions',
			'Arrays Multidimensional',
			'JS Objects',
			'JS Objects-II',
			'Callback & HOF',
			'Javascript DOM',
			'DOM - Events',
			'JS DOM-Attributes',
		],
	},
	{
		themeColor: '#D6E2FA',
		name: 'Advanced JS',
		description:
			"This module takes your JavaScript knowledge further, focusing on advanced concepts such as concurrency, prototype inheritance, ES6 features, and new syntax to write more efficient code. You'll also explore how to manage state and handle pagination in applications. A quick look into the topics we will be covering: ",
		duration: '16.5 hours',
		topics: [
			'Concurrency - event loop',
			'Call Apply Bind',
			'Prototypal Inheritance',
			'ES6 - 1',
			'ES6 - 2',
			'ES6 - Arrow function',
			'Class - ES6',
			'promises, fetch, async, await',
			'Closures',
			'Sets',
			'Pagination - JS',
		],
	},
	{
		themeColor: '#FFF3CC',
		name: 'React',
		description:
			"React is a powerful JavaScript library for building user interfaces. This section introduces React basics, components, state management, and lifecycle methods. You'll learn about React's ecosystem, including Redux for state management and Context API for prop drilling, and how to test your React applications. A quick look into the topics we will be covering: ",
		duration: '37.5 hours',
		topics: [
			'React Intro',
			'Webpack, Babel, JSX, React',
			'React-I',
			'React - components, state, props',
			'React - styling, handling props',
			'React - list - keys',
			'React - Event handling - Conditional Rendering',
			'React - Forms - controlled - uncontrolled',
			'React - Lifecycle methods',
			'React - state management and uplifting',
			'React - Composition',
			'React - Context API',
			'React - Styled Components',
			'React Components and Proptypes',
			'Redux - Intro',
			'Redux - 1',
			'React - Redux',
			'Redux - configuring store',
			'Redux - thunks',
			'React - Hooks',
			'React - Hooks - useContext, Custom Hooks',
			'React - Hooks - Libraries',
			'React - memo, useCallback',
			'React - Testing',
			'React - Redux - Testing',
		],
	},
	{
		themeColor: '#DCFFCC',
		name: 'NEM',
		description:
			'Learn backend development with the NEM stack, covering Node.js for server-side scripting, Express.js for building web applications, and MongoDB for database management. Topics like authentication, REST APIs, MVC architecture, and cloud services with AWS are included. A quick look into the topics we will be covering: ',
		duration: '28.5 hours',
		topics: [
			'Introduction to Node.js',
			'Intro to Node - 2',
			'Intro to Express.JS',
			'Intro to Mongo DB',
			'Mongo - 2',
			'Mongo - Array update queries, Aggregation Pipelines',
			'Mongoose, Express, Node',
			'Mongoose Validations',
			'Mongoose Express',
			'MVC',
			'File Upload',
			'Authentication',
			'Pagination',
			'Authorization',
			'Database Indexing',
			'Data Modelling',
			'Websockets',
			'Intro to AWS Services',
			'Intro to AWS - 2',
		],
	},
];
const daModules = [
	{
		themeColor: '#FEDFE5',
		name: 'SQL',
		description:
			"This module is an introduction to the essential database language, SQL (Structured Query Language). Starting with database basics, you'll quickly move on to master data manipulation and querying techniques, such as reading, updating, and deleting records. Learn how to structure complex queries, use aggregate functions, and manage databases efficiently. A quick look into the topics we will be covering: ",
		duration: '12 hours',
		topics: [
			'Introduction to Databases',
			'Read, Update & Delete',
			'Drop, Where & Count',
			'Like, Distinct & Aggregate',
			'Min, Max, Grouping & Filtering',
			'Joins & Aggregations With Joins',
			'Sub Queries',
			'Set Operators & Case When',
		],
	},
	{
		themeColor: '#CFE9EE',
		name: 'Excel',
		description:
			"In this module, you'll learn the essentials of Microsoft Excel, from basic operations and formatting to advanced functions like PivotTables and Power Query. It's designed to equip you with the skills to analyze data effectively, visualize information, and utilize Excel's powerful data manipulation tools. A quick look into the topics we will be covering:",
		duration: '15 hours',
		topics: [
			'Introduction to Excel',
			'Conditional Formatting and Custom Formatting',
			'Operators, Fill options & Cell Referencing',
			'Text Functions, Date Functions, Nested IF',
			'Excel Table, Charts',
			'Lookup Functions and Reference Functions',
			"Pivot Table and it's features",
			'Charts and Pivot Charts',
			'Power Query and Power Pivot',
		],
	},
	{
		themeColor: '#D6E2FA',
		name: 'Python',
		description:
			"Python is a versatile programming language you'll master in this module. Starting with the basics, you'll move on to complex data types, control structures, functions, and modules. Additionally, you'll explore Python's powerful libraries like NumPy and Pandas for data analysis. A quick look into the topics we will be covering: ",
		duration: '22.5 hours',
		topics: [
			'Python Introduction',
			'Datatypes and Variables in Python',
			'Operators and Conditional Statements',
			'Nested Conditional Statements, Introduction to Loops',
			'Loops and Nested Loops',
			'List & Tuples',
			'Strings, Functions',
			'Sets and Dictionaries',
			'Modules and Exception Handling',
			'Modules and Packages',
			'Introduction to Nump',
			'yNumpy Operations',
			'Introduction to Pandas',
			'Data Pre-Processing',
			'Data Cleaning',
		],
	},
	{
		themeColor: '#FFF3CC',
		name: 'Guesstimates & Business Scenarios',
		description:
			"This unique module trains you to estimate and solve business problems analytically. You'll learn how to make educated guesses ('guesstimates') and navigate through complex business scenario cases, developing skills essential for strategic decision-making and problem-solving in a business context. A quick look into the topics we will be covering: ",
		duration: '12 hours',
		topics: [
			'Guesstimates 1',
			'Guesstimates 2',
			'Guesstimates 3',
			'Guesstimates 4',
			'Business Scenario Cases 1',
			'Business Scenario Cases 2',
			'Business Scenario Cases 3',
			'Business Scenario Cases 4',
		],
	},
	{
		themeColor: '#DCFFCC',
		name: 'Power BI',
		description:
			"This module takes you through the capabilities of Power BI, a leading analytics tool, to transform raw data into informative insights. You'll learn to build interactive reports, dashboards, and learn advanced data manipulation techniques to become proficient in data analysis and business intelligence. A quick look into the topics we will be covering: ",
		duration: '12 hours',
		topics: ['Power BI 1', 'Power BI 2', 'Power BI 3', 'Power BI 4', 'Power BI 5', 'Power BI 6', 'Power BI 7', 'Power BI 8'],
	},
	{
		themeColor: '#FFCCFD',
		name: 'Tableau',
		description:
			'Strengthen your foundational knowledge of probability and statistics in this module. It covers a wide range of topics from statistical inference and set theory to probability distributions and hypothesis testing, equipping you with the quantitative skills to analyze and interpret data effectively. A quick look into the topics we will be covering: ',
		duration: '7.5 hours',
		topics: [
			'Introduction to Tableau',
			'Charts and Data source filters',
			'Functions and Operators',
			'LOD functions, Parameters, Groups & sets',
			'Dashboard & Story boards',
		],
	},
	{
		themeColor: '#FFDBCC',
		name: 'Probability & Stats',
		description:
			'Strengthen your foundational knowledge of probability and statistics in this module. It covers a wide range of topics from statistical inference and set theory to probability distributions and hypothesis testing, equipping you with the quantitative skills to analyze and interpret data effectively. A quick look into the topics we will be covering: ',
		duration: '12 hours',
		topics: [
			'Statistical Inference Frequency Distributions Descriptive Measures',
			'De Morgan’s Law Set Theory',
			'Conditional probability Bayes theorem',
			"Bernoulli's random variable",
			'Cumulative Distribution function Binomial random variable Mean and variance',
			'Continuous Random Variables',
			'Normal Distribution',
		],
	},
	{
		themeColor: '#CCF3FF',
		name: 'Machine Learning',
		description:
			"This module introduces the concepts and algorithms of machine learning, starting with the basics of linear regression to more complex topics like ensemble techniques and natural language processing (NLP). You'll learn both the theory and practical application of machine learning models to real-world problems. A quick look into the topics we will be covering:",
		duration: '16.5 hours',
		topics: [
			'Introduction to MLLinear Regression',
			'Optimization',
			'Linear Regression',
			'Polynomial Regression, Bias Variance Tradeoff',
			'Logistic Regression',
			'Logistic Regression-II',
			'Decision Tree',
			'Ensemble Techniques - Random Forest',
			'PCA',
			'NLP Basics',
		],
	},
];
const ModuleCard = ({ module, index }: { module: any; index: number }) => {
	return (
		<Box border={'1px solid #D9D9D9'} borderRadius={'16px'} p={'16px'}>
			<Box display={'flex'} alignItems={'center'} gap={'2'}>
				<Box
					borderRadius={'50%'}
					bg={module.themeColor || '#FEDFE5'}
					width={'40px'}
					height={'40px'}
					display={'flex'}
					justifyContent={'center'}
					alignItems={'center'}
				>
					<Text as={'h4'}> {index + 1}</Text>
				</Box>
				<Text as={'h6'}> {module.name}</Text>
			</Box>
			<Box ml={'40px'} display={'flex'} flexDirection={'column'} gap={2}>
				<Text color={'#3B3435'} fontSize={'16px'}>
					Duration: {module.duration}
				</Text>
				<Text fontSize={'14px'}>{module.description}</Text>

				<UnorderedList gap={'2'} display={'flex'} flexDirection={'column'}>
					{module.topics.map((topic: string) => (
						<ListItem fontWeight={'700'} color={'#0A0103'}>
							{topic}
						</ListItem>
					))}
				</UnorderedList>
			</Box>
		</Box>
	);
};
export const CourseSyllabus = () => {
	const navigate = useNavigate();
	const snackbar = useSnackBar();
	const courseId = useParams<{ courseId: string }>().courseId;
	const { data: courses } = useAllCourses();
	const { data: publishedItems, refetch: refetchPublishedItems } = usePublishedItems(courseId);
	const { data: userProfile, refetch: refetchUserProfile } = useUserProfile();
	const course = courses?.find(course => course._id === courseId);
	const currModule = course?.name === 'FSD_GEN_AI - SAGEX' ? fsdModules : daModules;
	const [currCourseStarted, setCurrCourseStarted] = React.useState(false);
	const [roadmapPublishedItem, setRoadmapPublishedItem] = React.useState<any>();
	const isMobile = useBreakpointValue({ base: true, md: false });
	const { data: domainConfig } = useDomainConfig({ uiHost: window.location.host });
	const { mutate: startCourseMutate, isLoading: isStartCourseLoading } = useMutation(startCourse, {
		onSuccess: data => {
			refetchUserProfile();
		},
		onError: error => {
			const e = axiosErrorHandler(error);
			if (typeof e === 'object' && 'message' in e) {
				snackbar.error(e.message);
			} else {
				snackbar.error('Something went wrong');
			}
		},
	});
	React.useEffect(() => {
		// filter first published item of type Roadmap
		if (!publishedItems) return;
		const roadmap = publishedItems.find(item => item.entityType === 'Roadmap');
		// @ts-ignore
		setRoadmapPublishedItem(roadmap);
	}, [publishedItems]);
	React.useEffect(() => {
		userProfile?.enrolments.forEach((enrolment: { courseStatus: any; course: any }) => {
			if (enrolment?.course === courseId && enrolment.courseStatus === 'Started') {
				setCurrCourseStarted(true);
			}
		});
	}, [courseId, userProfile]);

	useEffect(() => {
		if (domainConfig?.key !== SAGEX_ORG_KEY) {
			navigate('/dashboard');
		}
	}, [domainConfig, navigate]);

	const handleStartCourse = (courseId: string) => {
		startCourseMutate({ courseId });
	};
	return (
		<Box display={'flex'} flexDirection={'column'} gap={'16px'} m={{ md: '40px', base: '10px' }} mb={'0px'}>
			<Text as={isMobile ? 'h4' : 'h2'}>{course?.name}</Text>
			<Text as={isMobile ? 'h5' : 'h3'} color="#6E71CC">
				Syllabus
			</Text>
			<Box
				borderTop={'1px solid #F3F2F2'}
				py={'18px'}
				display={'flex'}
				flexDirection={'column'}
				gap={4}
				overflow={'scroll'}
				// h={'67vh'}
				maxW={'1120px'}
			>
				{currModule.map((module, index) => (
					<ModuleCard module={module} index={index} />
				))}
			</Box>
			<Box
				position={'sticky'}
				bottom={0}
				display={'flex'}
				justifyContent={'end'}
				gap={'2'}
				zIndex={2}
				bg={'white'}
				pb={'30px'}
				borderTop={'1px solid #D9D9D9'}
			>
				<Button
					mt="16px"
					color={'#4358F6'}
					_hover={{ bg: '#4358f624' }}
					variant={'outline'}
					textStyle="btn-md"
					size={{ base: 'md', md: 'lg' }}
					onClick={() => {
						// GO BACK TO PREVIOUS URL IN STACK
						navigate('/dashboard');
					}}
				>
					Go Back
				</Button>
				<Button
					mt="16px"
					bg="#4358F6"
					variant="primary"
					textStyle="btn-md"
					size={{ base: 'md', md: 'lg' }}
					onClick={() => {
						if (!currCourseStarted) {
							if (course?._id) {
								handleStartCourse(course?._id);
							}
						}
						navigate(`/dashboard/published-item/${roadmapPublishedItem?._id}`);
					}}
				>
					{currCourseStarted ? 'Continue Course' : 'START COURSE'}
				</Button>
			</Box>
		</Box>
	);
};
